import type { Mountaya } from "@mountaya/client";

import { bind } from "../console.gen";
import type { RestClientConfig } from "./sdk";
import { mountayaFor } from "./sdk";

/**
 * The console's half of the REST surface, bound to a config.
 *
 * `@mountaya/client` answers the operations the contract publishes. This answers the ones it
 * does not (secret keys, the organization record, the staff operations) and it exists because
 * those operations are real, typed, and called from this application, but must not be
 * reachable by anybody integrating with Mountaya. The split is expressed by which
 * document declares a path (`index.yaml` publishes, `index.internal.yaml` does not), and
 * both clients are generated from that one decision, so neither can drift from it.
 *
 * What this is NOT is a second transport. `bind` takes the very same `MountayaTransport` instance
 * the published client holds, so a console read and a studio read travel the same pipeline:
 * one credential projection, one retry policy, one date revival, one error taxonomy. A
 * refusal reads the same whichever client's operation answered it.
 */
type ConsoleClient = ReturnType<typeof bind>;

/**
 * clients memoizes one bound object per underlying SDK client.
 *
 * `bind` allocates a fresh three-level object of ~48 closures, and a query-fn reaches this
 * on every call. Keying the memo on the client instance rather than on the config means it
 * follows `mountayaFor`'s own cache exactly: the day that cache evicts, this one does too,
 * with nothing here needing to know the key's shape. A `WeakMap` so a client dropped by that
 * cache takes its bound object with it.
 */
const clients = new WeakMap<Mountaya, ConsoleClient>();

/**
 * consoleFor returns the console client for a config, binding it once.
 *
 * The surface is always `"rest"`: every console operation is served by the REST API. There
 * is no parameter for it, unlike `mountayaFor`, because there is no console operation on the
 * tile or Data surfaces and inventing the argument would only invite one.
 */
export const consoleFor = (config: RestClientConfig): ConsoleClient => {
  const client = mountayaFor(config);
  const existing = clients.get(client);
  if (existing !== undefined) {
    return existing;
  }

  const bound = bind(client.transport);
  clients.set(client, bound);

  return bound;
};
